// ============================================================
//  DashboardPage.js — Redesigned
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  listBots,
  createBot,
  deleteBot,
  toggleBot,
  cloneBot,
  getBillingOverview,
} from '../../api/client';
import toast from 'react-hot-toast';
import PhoneRegistrationWizard from '../bots/PhoneRegistrationWizard';
import './DashboardPage.css';

// ── Helpers ───────────────────────────────────────────────────
function initials(name = '') {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ── Stat card ─────────────────────────────────────────────────
function StatCard({ label, value, sub, accent = 'accent-neutral', iconClass = 'gray', icon }) {
  return (
    <div className={`stat-card ${accent}`}>
      {icon && (
        <div className={`stat-icon ${iconClass}`} aria-hidden="true">
          {icon}
        </div>
      )}
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// ── Platform badge ────────────────────────────────────────────
function PlatformBadge({ platform }) {
  const label =
    platform === 'whatsapp'
      ? 'WhatsApp'
      : platform
      ? platform.charAt(0).toUpperCase() + platform.slice(1)
      : 'Unknown';
  return (
    <span className={`platform-badge platform-${platform ?? 'unknown'}`}>
      {label}
    </span>
  );
}

// ── Bot card ──────────────────────────────────────────────────
function BotCard({ bot, onToggle, onDelete, onClone, onClick }) {
  const [toggling, setToggling] = useState(false);
  const isActive = bot.bIsActive == 1;

  async function handleToggle(e) {
    e.stopPropagation();
    setToggling(true);
    await onToggle(bot.bId, !isActive);
    setToggling(false);
  }

  return (
    <div
      className="bot-card"
      onClick={() => onClick(bot.bId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(bot.bId)}
    >
      {/* Header */}
      <div className="bot-card-header">
        <div className="bot-avatar" aria-hidden="true">
          {initials(bot.bName) || 'B'}
        </div>

        <div className="bot-info">
          <div className="bot-name">{bot.bName}</div>
          <div className="bot-id">#{bot.bId}</div>
        </div>

        <span
          className="toggle-wrap"
          onClick={handleToggle}
          role="switch"
          aria-checked={isActive}
          aria-label={`${isActive ? 'Deactivate' : 'Activate'} ${bot.bName}`}
          title={isActive ? 'Deactivate' : 'Activate'}
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleToggle(e)}
        >
          <span className={`toggle-track ${isActive ? 'on' : 'off'} ${toggling ? 'toggling' : ''}`}>
            <span className="toggle-knob" />
          </span>
        </span>
      </div>

      {/* Body */}
      <div className="bot-card-body">
        <PlatformBadge platform={bot.bPlatform} />
        <div className="bot-stats-row">
          <span className="bot-stat-item">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            {bot.totalCommands ?? 0}
          </span>
          <span className="bot-stat-item">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            {(bot.totalUsers ?? 0).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="bot-card-footer" onClick={(e) => e.stopPropagation()}>
        <button
          className="bot-action-btn"
          onClick={() => onClone(bot.bId)}
          aria-label={`Clone ${bot.bName}`}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          Clone
        </button>
        <button
          className="bot-action-btn danger"
          onClick={() => onDelete(bot.bId, bot.bName)}
          aria-label={`Delete ${bot.bName}`}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
          Delete
        </button>
      </div>
    </div>
  );
}

// ── Create Bot modal ──────────────────────────────────────────
function CreateBotModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    bName: '',
    bLanguage: 'json',
    bToken: '',
    bPhoneNumberId: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPhoneWizard, setShowPhoneWizard] = useState(false);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.bName.trim())          return toast.error('Bot name is required');
    if (!form.bToken.trim())         return toast.error('WhatsApp token is required');
    if (!form.bPhoneNumberId.trim()) return toast.error('Phone number ID is required');

    setLoading(true);
    try {
      const res = await createBot({ ...form, bPlatform: 'whatsapp' });
      if (res.data?.success) {
        toast.success('Bot created!');
        onCreated(res.data.bId);
      } else {
        toast.error(res.data?.message ?? 'Failed to create bot');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setLoading(false);
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <>
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-modal-title"
    >
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title" id="create-modal-title">Create new bot</div>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body" noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="bName">Bot name</label>
            <input
              id="bName"
              className="form-input"
              placeholder="e.g. Customer support bot"
              value={form.bName}
              onChange={(e) => set('bName', e.target.value)}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Platform</label>
            <div
              className="form-input"
              style={{ background: 'var(--surface)', color: 'var(--text-muted)', cursor: 'default' }}
            >
              WhatsApp (Cloud API)
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="bLanguage">Default command language</label>
            <select
              id="bLanguage"
              className="form-input form-select"
              value={form.bLanguage}
              onChange={(e) => set('bLanguage', e.target.value)}
            >
              <option value="json">JSON</option>
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
            </select>
            <div className="form-hint">
              Pre-selects this language whenever you create a new Command on this bot. You can still pick a different language per command, and Custom Endpoints have their own separate language picker.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="bToken">WhatsApp token</label>
            <input
              id="bToken"
              className="form-input"
              placeholder="EAAxxxx…"
              value={form.bToken}
              onChange={(e) => set('bToken', e.target.value)}
            />
            <div className="form-hint">
              From Meta Business → WhatsApp → API Setup
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="bPhoneNumberId">Phone number ID</label>
            <input
              id="bPhoneNumberId"
              className="form-input"
              placeholder="123456789012345"
              value={form.bPhoneNumberId}
              onChange={(e) => set('bPhoneNumberId', e.target.value)}
            />
            <div className="form-hint">
              Found next to your number in WhatsApp API Setup —{' '}
              <button type="button" className="link-btn" style={{ fontSize: 12, padding: 0 }}
                onClick={() => setShowPhoneWizard(true)}>
                don't have a number yet? Register one →
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <><span className="spinner" aria-hidden="true" /> Creating…</>
              ) : (
                'Create bot'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>

    {showPhoneWizard && (
      <PhoneRegistrationWizard
        initialToken={form.bToken}
        onClose={() => setShowPhoneWizard(false)}
        onComplete={({ phoneNumberId, token }) => {
          setForm(f => ({ ...f, bPhoneNumberId: phoneNumberId, bToken: token || f.bToken }));
          setShowPhoneWizard(false);
          toast.success('Number added — continue creating your bot below.');
        }}
      />
    )}
    </>
  );
}

// ── Delete confirm modal ──────────────────────────────────────
function DeleteModal({ botName, onConfirm, onClose }) {
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
    >
      <div className="modal modal-sm">
        <div className="modal-header">
          <div className="modal-title" id="delete-modal-title">Delete bot</div>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body">
          <div className="delete-warning">
            This will permanently delete <strong>{botName}</strong> along with all
            its commands, panel fields, and logs. This cannot be undone.
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-danger" onClick={onConfirm}>Delete bot</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Search icon SVG ───────────────────────────────────────────
function SearchIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function DashboardPage() {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [bots, setBots]                 = useState([]);
  const [stats, setStats]               = useState(null);
  const [plan, setPlan]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filter, setFilter]             = useState('all');
  const [creating, setCreating]         = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [botsRes, billingRes] = await Promise.all([
        listBots(),
        getBillingOverview(),
      ]);
      if (botsRes.data?.success) {
        setBots(botsRes.data.bots ?? []);
        setStats(botsRes.data.stats ?? null);
      }
      if (billingRes.data?.current_plan) {
        setPlan(billingRes.data.current_plan);
      }
    } catch {
      /* axios interceptor handles 401 */
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived values ────────────────────────────────────────
  const maxBots  = plan?.plMaxBots != null ? Number(plan.plMaxBots) : null;
  const atLimit  = maxBots !== null && bots.length >= maxBots;
  const planName = plan?.plName ?? 'Free';

  const billingIssueBots = bots.filter((b) => b.bPaymentIssue == 1);

  const filtered = bots.filter((b) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      b.bName?.toLowerCase().includes(q) ||
      b.bId?.toLowerCase().includes(q);
    const matchFilter =
      filter === 'all' ||
      (filter === 'active'   &&  b.bIsActive == 1) ||
      (filter === 'inactive' &&  b.bIsActive != 1);
    return matchSearch && matchFilter;
  });

  // ── Handlers ──────────────────────────────────────────────
  async function handleToggle(bId, active) {
    try {
      await toggleBot(bId, active);
      setBots((prev) =>
        prev.map((b) => (b.bId === bId ? { ...b, bIsActive: active ? 1 : 0 } : b))
      );
    } catch {
      toast.error('Failed to update bot status');
    }
  }

  function handleDelete(bId, bName) {
    setDeleteTarget({ bId, bName });
  }

  async function confirmDelete() {
    try {
      await deleteBot(deleteTarget.bId);
      setBots((prev) => prev.filter((b) => b.bId !== deleteTarget.bId));
      toast.success('Bot deleted');
    } catch {
      toast.error('Failed to delete bot');
    }
    setDeleteTarget(null);
  }

  async function handleClone(bId) {
    toast.loading('Cloning bot…', { id: 'clone' });
    try {
      const res = await cloneBot(bId);
      if (res.data?.success) {
        toast.success('Bot cloned!', { id: 'clone' });
        load();
      } else {
        toast.error(res.data?.message ?? 'Clone failed', { id: 'clone' });
      }
    } catch {
      toast.error('Network error', { id: 'clone' });
    }
  }

  function handleCreated(bId) {
    setCreating(false);
    navigate(`/bots/${bId}`);
  }

  function handleCreateClick() {
    if (atLimit) {
      toast.error(
        `Your ${planName} plan allows up to ${maxBots} bot${maxBots === 1 ? '' : 's'}. Upgrade to add more.`,
        { duration: 5000 }
      );
      navigate('/billing');
      return;
    }
    setCreating(true);
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="dashboard">

      {billingIssueBots.length > 0 && (
        <div
          role="alert"
          style={{
            background: '#fff4f4',
            border: '1px solid #f3b9b9',
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden="true">⚠️</span>
          <div>
            <strong style={{ color: '#b42318' }}>
              WhatsApp payment issue — messages are not being delivered
            </strong>
            <p style={{ margin: '4px 0 8px', color: '#7a2e2e' }}>
              {billingIssueBots.length === 1
                ? `Your bot "${billingIssueBots[0].bName}" has an unsettled payment on its WhatsApp Business account. Meta is blocking outbound template messages until this is resolved.`
                : `${billingIssueBots.length} of your bots have an unsettled payment on their WhatsApp Business account. Meta is blocking outbound template messages until this is resolved.`}
            </p>
            <a
              href="https://business.facebook.com/billing_hub"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ display: 'inline-block', fontSize: 13, padding: '6px 14px' }}
            >
              Resolve billing on Meta →
            </a>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="dash-header">
        <div className="dash-title-group">
          <h1 className="dash-title">Dashboard</h1>
          <p className="dash-sub">Welcome back, {user?.sFname} 👋</p>
        </div>

        <div className="dash-header-actions">
          <button
            className={`btn ${atLimit ? 'btn-outline' : 'btn-primary'}`}
            onClick={handleCreateClick}
            title={atLimit ? `Upgrade to create more than ${maxBots} bots` : 'Create a new bot'}
          >
            {atLimit ? (
              <>🔒 Limit reached</>
            ) : (
              <>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className="btn-icon"
                  aria-hidden="true"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Create bot
              </>
            )}
          </button>
          {maxBots !== null && (
            <span className={`bot-count-pill ${atLimit ? 'at-limit' : ''}`}>
              {bots.length} / {maxBots} bots used
            </span>
          )}
        </div>
      </div>

      {/* Limit banner */}
      {atLimit && (
        <div className="limit-banner" role="alert">
          <span className="limit-banner-icon">⚠️</span>
          <span className="limit-banner-text">
            <strong>{planName} plan limit reached.</strong>{' '}
            You've used all {maxBots} bot slot{maxBots === 1 ? '' : 's'}.{' '}
            <button className="link-btn" onClick={() => navigate('/billing')}>
              Upgrade to add more →
            </button>
          </span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="stats-row">
          <StatCard
            label="Total bots"
            value={stats.total}
            accent="accent-primary"
            iconClass="green"
            icon={
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="10" rx="2" />
                <circle cx="12" cy="5" r="2" />
                <path d="M12 7v4" />
                <line x1="8" y1="16" x2="8" y2="16" strokeWidth="3" strokeLinecap="round" />
                <line x1="16" y1="16" x2="16" y2="16" strokeWidth="3" strokeLinecap="round" />
              </svg>
            }
          />
          <StatCard
            label="Active"
            value={stats.active}
            sub={`${stats.inactive} inactive`}
            accent="accent-success"
            iconClass="teal"
            icon={
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            }
          />
          <StatCard
            label="Total users"
            value={(stats.totalUsers ?? 0).toLocaleString()}
            accent="accent-neutral"
            iconClass="gray"
            icon={
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
          />
          {maxBots !== null && (
            <StatCard
              label="Bot limit"
              value={`${bots.length}/${maxBots}`}
              sub={atLimit ? 'Limit reached — upgrade' : `${maxBots - bots.length} remaining`}
              accent={atLimit ? 'accent-danger' : 'accent-neutral'}
              iconClass={atLimit ? 'red' : 'gray'}
              icon={
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              }
            />
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="bots-toolbar">
        <div className="search-wrap">
          <span className="search-icon">
            <SearchIcon />
          </span>
          <input
            className="search-input"
            type="search"
            placeholder="Search bots…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search bots"
          />
        </div>

        <div className="filter-tabs" role="group" aria-label="Filter bots">
          {['all', 'active', 'inactive'].map((f) => (
            <button
              key={f}
              className={`filter-tab ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Bot list */}
      {loading ? (
        <div className="bots-loading" role="status" aria-live="polite">
          <span className="spinner spinner-dark" aria-hidden="true" />
          <span>Loading bots…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bots-empty" role="status">
          <div className="bots-empty-icon" aria-hidden="true">🤖</div>
          {bots.length === 0 ? (
            <>
              <span>No bots yet.</span>
              <button className="link-btn" onClick={handleCreateClick}>
                Create your first bot →
              </button>
            </>
          ) : (
            <span>No bots match your search.</span>
          )}
        </div>
      ) : (
        <>
          {(search || filter !== 'all') && (
            <p className="section-label">
              {filtered.length} bot{filtered.length !== 1 ? 's' : ''} found
            </p>
          )}
          <div className="bots-grid">
            {filtered.map((bot) => (
              <BotCard
                key={bot.bId}
                bot={bot}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onClone={handleClone}
                onClick={(bId) => navigate(`/bots/${bId}`)}
              />
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      {creating && (
        <CreateBotModal
          onClose={() => setCreating(false)}
          onCreated={handleCreated}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          botName={deleteTarget.bName}
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}